import type { WorkflowError, TableNode } from '..';
// Both VALUE imports come from the table-store module DIRECTLY, not via the `..`
// barrel: this file sits in a require cycle (table-store → utils → here → `..` →
// table-store), and the barrel's `export *` re-export leaves an enum/object
// binding undefined when `findNodeNumberProblem` runs inside the `canPublish`
// computed. The direct specifier resolves the live binding correctly. The types
// above are erased at compile time and so cannot participate in the cycle at
// all — which is why they may still come from the barrel.
import { NodeColumns, platformFieldNameToNodeColumnMap } from '../table-store';
import { BotCompilerError } from '../../utils';
import {
  resolveErrorColIndex,
  resolveErrorRowIndex,
} from './resolve-error-cell-address';
// The canonical group-header guard. Case-INSENSITIVE on purpose: legacy/imported
// data may carry 'G' while the enum is 'g', and a case-sensitive compare lets
// such a row leak past every group-aware boundary (PSTUDIO-3159). That leak used
// to be harmless here (a group header holds `nodeNumber: null`, which the
// duplicate detector skipped anyway); with the blank-number check below it
// INVERTS into a false accusation that can never be cleared from the UI, because
// a group header has no editable Node # cell.
import { isGroupRow } from './group-row-guards';
// One definition of "does this row have a number", shared with the store's
// message helpers and the publish gate (PSTUDIO-3990).
import {
  isBlankNodeNumberValue,
  nodeNumberOrNull,
  toNodeNumberIdentity,
} from './node-number-identity';

const DUPLICATE_NODE_NUMBER_MESSAGE =
  'Duplicate node number. Node numbers must be unique.';

const DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API = {
  NODE_NUMBER: 'node number',
};

/**
 * Does this backend message state the duplicate-node-number problem?
 *
 * Matched by MEANING, not by exact string: the two validators word it
 * differently and one interpolates the offending number, so an equality check
 * can never cover them —
 *   "Bot has duplicate node numbers. This is not allowed. Please fix."   (bot compiler)
 *   "This node number '2' is duplicated which is not allowed."           (agent validator / PIF)
 * The old equality check only knew the first, so the second reached the popover
 * verbatim and sat there as a third row beside our own two per-cell errors
 * (PSTUDIO-4000, observed live). Both markers are required, so an unrelated
 * message mentioning only one of them is left untouched.
 */
const isApiDuplicateNodeNumberMessage = (message: string): boolean => {
  const text = (message ?? '').toLowerCase();
  return text.includes('duplicat') && text.includes('node number');
};

const MISSING_NODE_NUMBER_MESSAGE =
  'Node number is missing. Enter a whole number.';

/** Fixed tail of the invalid-value message. The value itself is interpolated in
 *  front of it, so the tail — together with `colId` — is what lets the next pass
 *  recognise its OWN previous output without a loose substring match that could
 *  swallow a backend message.
 *
 *  "not a USABLE node number … other than 0", not "not a whole number": this one
 *  message has to be true of `'12.5'` AND of `0`, and 0 IS a whole number — it
 *  is simply not a node number (it is `sanitizeNodeNumber`'s fallback for an
 *  unreadable cell). The publish toast is worded from the same two facts, so the
 *  two cannot contradict each other (PSTUDIO-3990). */
const INVALID_NODE_NUMBER_TAIL =
  ' is not a usable node number. Enter a whole number other than 0.';

/** How much of the offending value is worth quoting back. A `Node #` cell can
 *  hold whatever a paste or a CSV import put there, and the whole value would
 *  otherwise be re-interpolated on every keystroke inside a MobX computed, and
 *  read out in full by a screen reader from the entry's accessible name. The
 *  cell is truncated visually anyway, so nothing is lost by truncating here. */
const MAX_QUOTED_VALUE_LENGTH = 80;

const quoteOffendingValue = (raw: unknown): string => {
  const text = String(raw);
  if (text.length <= MAX_QUOTED_VALUE_LENGTH) return text;
  // Cut on a code-point boundary. `slice` counts UTF-16 code units, so a cut
  // landing between an astral character's surrogates would keep only the high
  // one — a lone surrogate that reaches the popover caption AND the cell's
  // accessible name verbatim, where it renders as a broken glyph instead of a
  // clean truncation. Stepping back one unit drops the whole pair.
  const cut = text.slice(0, MAX_QUOTED_VALUE_LENGTH);
  const lastUnit = cut.charCodeAt(cut.length - 1);
  const endsMidSurrogatePair = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
  return `${endsMidSurrogatePair ? cut.slice(0, -1) : cut}…`;
};

const invalidNodeNumberMessage = (raw: unknown): string =>
  `"${quoteOffendingValue(raw)}"${INVALID_NODE_NUMBER_TAIL}`;

/** An error this module emitted itself, and may therefore replace. Matched on
 *  an exact message / an exact fixed tail — never a bare substring, which would
 *  let a backend error using the same words disappear.
 *
 *  The column is checked permissively on purpose: our own errors always carry
 *  `nodeNumber`, but a NORMALISED backend duplicate does not necessarily — the
 *  bot compiler reports the problem with an empty `column` and no `field_name`,
 *  so `buildWorkflowErrors` leaves its `colId` undefined while rewriting the
 *  message to ours. Demanding `colId === nodeNumber` would fail to strip that
 *  one and put the server's copy next to our per-cell pair, which is the
 *  double-count PSTUDIO-4000 removed. An error naming a DIFFERENT column is
 *  still left alone. */
const isOwnNodeNumberError = (e: WorkflowError): boolean => {
  if (e.colId != null && e.colId !== NodeColumns.nodeNumber) return false;
  const message = e.errorMessage ?? '';
  return (
    message === DUPLICATE_NODE_NUMBER_MESSAGE ||
    message === MISSING_NODE_NUMBER_MESSAGE ||
    message.endsWith(INVALID_NODE_NUMBER_TAIL)
  );
};

/**
 * The one resolver the derivation needs from the store. REQUIRED, deliberately:
 * an optional parameter defaulting to the `tableStore` barrel binding is the
 * PSTUDIO-4000 trap — inside a store computed that binding is still undefined
 * and the read throws. Making it mandatory means the compiler, not a comment,
 * stops the next caller from re-introducing it.
 *
 * Note what is NOT in here: a resolver for the row number the user SEES. An
 * earlier revision passed `getRowDisplayNumber` so the popover title could
 * address the row, and it made this derivation depend on `displayNumberById` →
 * `getAllSortedRows()`. That pulled the whole sorted row list into the
 * dependencies of a computed that only ever asked about node numbers. The
 * consequence that reproduces independently: on a 5,000-row table a
 * drag-reorder — which rewrites only `order` and changes neither a number nor
 * an error — re-ran the sort and the derivation on EVERY write, ~0.008 ms →
 * ~8 ms per write of pure dead work (measured twice, by different people,
 * against a reconstruction of the reverted revision). Editing cost rose too,
 * because `getRowDisplayNumber` is a `computedFn` and a table of duplicates
 * allocates one computed node per offending row per pass; how large that second
 * effect is depends on how the reconstruction is written, so no figure for it
 * is quoted here. The display number is needed only when the popover is OPEN,
 * so it is resolved there, from `nodeId`, at render time — see
 * `buildErrorRowProps` in `validation-popover.utils.ts`.
 */
export interface NodeNumberErrorContext {
  /** MUST be injected when called from a store computed: reading the
   *  `tableStore` binding off the `..` barrel throws there, because this file
   *  sits in the require cycle table-store → utils → here → `..` → table-store. */
  getColumnIndex: (colId: NodeColumns) => number;
}

/**
 * Derive BOTH node-number error classes from the current rows in ONE pass.
 *
 * They are two sides of one mechanism — a number that is missing and a number
 * that collides — so they are derived together and can never be wired to
 * different call sites (`.claude/rules/cross-cutting-invariants.md`). Doing it
 * in a single traversal also keeps the cost where PSTUDIO-4000 left it: the
 * store recomputes this for `errors` AND `pifErrors`, so a second full pass per
 * class would have quadrupled the per-keystroke work on a large table.
 *
 * Like its predecessor it STRIPS its own previous output from `existingErrors`
 * before re-emitting, so a recompile that also reported the problem cannot
 * double-count it.
 *
 * Neither class is capped. One error per offending row is what the duplicate
 * half has always emitted, and capping only the new half would have been an
 * asymmetry the data does not support — a CSV import or a RAW paste produces
 * thousands of duplicates exactly as readily as clearing a column produces
 * thousands of blanks. A cap is also not free: the eight error indexes the
 * table paints its highlights from are built from THIS list, so a capped list
 * silently stops highlighting the rows past the cap while the publish toast
 * still tells the user to "fix the highlighted cells". The unvirtualized
 * popover list is the real cost of a mass error state, it predates this ticket
 * on the duplicate side, and it is only paid while the popover is open —
 * tracked separately rather than half-solved here.
 */
export const deriveNodeNumberErrors = (
  rows: TableNode[],
  existingErrors: WorkflowError[],
  context: NodeNumberErrorContext
): WorkflowError[] => {
  const { getColumnIndex } = context;
  const colId = NodeColumns.nodeNumber;
  const colIndex = getColumnIndex(colId);
  const colIndexOrNull = colIndex === -1 ? null : colIndex;

  const byNodeNumber = new Map<
    string,
    { row: TableNode; rowIndex: number }[]
  >();
  const invalidRows: { row: TableNode; rowIndex: number }[] = [];

  // ONE traversal feeds both classes.
  rows.forEach((row, rowIndex) => {
    // Group headers are structural, not nodes: they hold `nodeNumber: null` by
    // design (`groupRows`), so they can neither duplicate nor be "missing".
    if (isGroupRow(row)) return;
    const identity = toNodeNumberIdentity(row[NodeColumns.nodeNumber]);
    if (identity === undefined) {
      invalidRows.push({ row, rowIndex });
      return;
    }
    const list = byNodeNumber.get(identity) ?? [];
    list.push({ row, rowIndex });
    byNodeNumber.set(identity, list);
  });

  const rowAddress = (row: TableNode, rowIndex: number) => ({
    colId,
    colIndex: colIndexOrNull,
    rowIndex,
    cellNumber: null,
    // Deliberately null, NOT `rowIndex + 1`. `rowIndex` is a position in the
    // insertion-ordered `rows`, while the table paints `getAllSortedRows()`
    // minus group headers — the two agree only on an untouched table, so
    // printing it would send the user to the wrong row after any drag or
    // grouping. The popover resolves the real number from `nodeId` at render
    // time; see the note on `NodeNumberErrorContext` for why it is not
    // resolved here.
    rowNumber: null,
    // Address the cell ID-FIRST, like every error source since PSTUDIO-3991:
    // the number-keyed indexes skip a null node number, so without the id a
    // numberless row would show an error and highlight nothing. It is also
    // what the popover resolves the printed row number and the click target
    // from.
    nodeId: (row[NodeColumns.id] as string | undefined) ?? null,
  });

  const generated: WorkflowError[] = [];

  byNodeNumber.forEach((entries) => {
    if (entries.length <= 1) return;
    entries.forEach(({ row, rowIndex }) => {
      generated.push({
        ...rowAddress(row, rowIndex),
        errorMessage: DUPLICATE_NODE_NUMBER_MESSAGE,
        // The duplicated value IS the row's real number, so naming it is correct
        // here — unlike the invalid case below.
        nodeNumber: toNodeNumberIdentity(row[NodeColumns.nodeNumber]) ?? null,
      });
    });
  });

  invalidRows.forEach(({ row, rowIndex }) => {
    const raw = row[NodeColumns.nodeNumber];
    // The SAME predicate the publish gate splits on, deliberately shared. The
    // two used to disagree on exactly one value, `0`: this message quoted it
    // ('"0" is not a valid node number') while the gate's toast called the row
    // "without a node number" — a claim the user could refute by looking at the
    // cell, which is the defect class this ticket is about. One definition now
    // answers "is the cell EMPTY?" for both: a 0 is present-but-unusable, so
    // both say the value cannot be used, and neither says it is absent.
    const isMissing = isBlankNodeNumberValue(raw);

    generated.push({
      ...rowAddress(row, rowIndex),
      errorMessage: isMissing
        ? MISSING_NODE_NUMBER_MESSAGE
        : invalidNodeNumberMessage(raw),
      // Deliberately null. `buildErrorLabel` prints `Node: <n>` for ANY non-nil
      // node number, so putting the offending `0` / `''` here is exactly how
      // "Node: 0" and the empty "Node: " reached the popover title — the defect
      // this ticket is about. With null the title addresses the ROW instead,
      // the only address a numberless row has.
      nodeNumber: null,
    });
  });

  return [
    ...existingErrors.filter((e) => !isOwnNodeNumberError(e)),
    ...generated,
  ];
};

/**
 * What, if anything, is wrong with the node numbers in `rows` — in ONE pass.
 *
 * This is the publish gate's synchronous answer, for `canPublish` (read on
 * every primary-nav render) and for the pre-publish guard in
 * `PublishModal.onPublish`. It reports WHICH problem it found, because the
 * guard's toast has to name the real cause: always saying "duplicate" is the
 * misleading message PSTUDIO-3990 is about.
 *
 * Two halves, one traversal, deliberately. Asking two separate predicates walks
 * the rows twice for a question a single walk answers (measured: 2.16 → 4.15 ms
 * per edit at 5,000 rows), and — worse — lets a future caller ask only one of
 * them. Tightening the duplicate predicate REMOVED a gate that existed only by
 * accident (two blank cells colliding under `Number('') === 0` and reading as a
 * duplicate of a number nobody typed), so the blank half is not an optional
 * extra here: without it this change would WEAKEN the gate rather than leave it
 * as it was.
 *
 * `duplicate` wins when both are present — it is the more specific diagnosis,
 * and it is what the pre-existing toast said.
 *
 * Allocation-free apart from the `Set`, and it deliberately does NOT skip
 * `hidden` rows: a row hidden by a collapsed group still ships to the backend,
 * so skipping it would hide a real defect and let a broken solution publish.
 * The short window where a soft-deleted row is still in `rows` is the accepted
 * cost of that choice.
 */
export type NodeNumberProblem = 'duplicate' | 'invalid-value' | 'missing';

export const findNodeNumberProblem = (
  rows: TableNode[]
): NodeNumberProblem | null => {
  const seen = new Set<string>();
  let sawMissing = false;
  let sawInvalidValue = false;

  for (const row of rows) {
    // A group header holds `nodeNumber: null` BY DESIGN (`groupRows`) and has
    // no editable Node # cell, so flagging one would block publishing forever
    // (PSTUDIO-3159). The guard is the canonical case-INSENSITIVE `isGroupRow`,
    // not a raw `=== G`: legacy/imported data may carry 'G'.
    if (isGroupRow(row)) continue;
    const raw = row[NodeColumns.nodeNumber];
    const identity = toNodeNumberIdentity(raw);
    if (identity === undefined) {
      // Split by the same predicate `deriveNodeNumberErrors` words its per-cell
      // message with. Collapsing both into one bucket is what told a user who
      // pasted '12.5' that their rows have NO number, while the cell's own error
      // said the value is not a valid one.
      if (isBlankNodeNumberValue(raw)) sawMissing = true;
      else sawInvalidValue = true;
      continue;
    }
    if (seen.has(identity)) return 'duplicate';
    seen.add(identity);
  }

  if (sawInvalidValue) return 'invalid-value';
  return sawMissing ? 'missing' : null;
};

export const buildWorkflowErrors = (
  errors: BotCompilerError[],
  rows: TableNode[],
  context: NodeNumberErrorContext
): WorkflowError[] => {
  const generatedErrors = errors
    .flatMap(
      ({
        err_msgs,
        cell_num,
        node_num,
        node_index,
        cell_index,
        row_num,
        column,
        node_id,
      }) => {
        // `nodeNumberOrNull`, not `?? null`: the compiler's sentinel for "no
        // node" is -1, but a row whose `Node #` cell is EMPTY reaches the
        // compiler as `Number('') === 0` (`prepareRowsDataForPifCompiler`), so
        // the diagnostic about that node comes BACK carrying `node_num: 0`. The
        // popover's `buildErrorLabel` prints `Node: <n>` for any non-nil value,
        // which is exactly how the phantom `Node: 0` this ticket removes reached
        // its title from the SERVER side — the client derivation had already
        // stopped producing it. Filtered by the one shared predicate rather than
        // a second local test, so the two paths cannot disagree about what a 0
        // in this column means. The -1 check stays alongside it, because -1 is a
        // legal node number here and only its sentinel POSITION distinguishes
        // the two (PSTUDIO-3990).
        const nodeNumber = node_num === -1 ? null : nodeNumberOrNull(node_num);
        const rowNumber = row_num ? row_num - 1 : null;
        const rIndex = row_num ? row_num - 2 : null;

        // The callback's return type is SPELLED OUT so the `item is
        // WorkflowError` guard below is legal: without it the inferred element
        // type is an object literal that is only structurally compatible, and
        // TypeScript rejects the predicate (TS2677).
        return err_msgs?.map(
          ({ field_name, error_description }): WorkflowError => {
            const columnKey =
              platformFieldNameToNodeColumnMap[field_name || ''];
            const colId = (column as NodeColumns) ?? columnKey;

            // Resolve the cell address id-first, so a content error whose node
            // number is blank still lands on its cell, and a valid index of 0 is
            // never swallowed by a falsy check (PSTUDIO-3991). The old
            // `node_index || rIndex || …` / `cell_index || …` chains did both.
            const colIndex = resolveErrorColIndex(
              { cellIndex: cell_index, colId },
              // Injected, not read off the barrel: same require-cycle reason as
              // the derivation context above (PSTUDIO-4000).
              (id) => context.getColumnIndex(id as NodeColumns)
            );
            const rowIndex = resolveErrorRowIndex(
              {
                nodeId: node_id,
                nodeNumber: node_num,
                nodeIndex: node_index,
                rowIndexFromRowNum: rIndex,
              },
              rows
            );

            return {
              colId,
              colIndex: colIndex === -1 ? null : colIndex,
              rowIndex: rowIndex === -1 ? null : rowIndex,
              errorMessage: error_description,
              cellNumber: cell_num === -1 ? null : cell_num,
              nodeNumber,
              rowNumber: row_num === -1 ? null : rowNumber,
              nodeId: node_id ?? null,
            };
          }
        );
      }
    )
    // A TYPE GUARD, and `!= null`: `err_msgs?.map(...)` yields `undefined` for a
    // diagnostic that carries no messages, `flatMap` does not flatten a
    // non-array, and `!== null` does not match `undefined` — so that hole
    // reached the `.some(({ errorMessage }) => …)` below and threw
    // `Cannot destructure property 'errorMessage' of 'undefined'`, taking the
    // whole validation pass and the popover with it. `BotCompilerError` declares
    // `err_msgs` as required, which is why nothing flagged it: the value comes
    // off the network, not from the compiler's own types.
    .filter((item): item is WorkflowError => item != null);

  const hasNodeNumberError = generatedErrors.some(
    ({ errorMessage }) =>
      errorMessage ===
        DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.NODE_NUMBER ||
      isApiDuplicateNodeNumberMessage(errorMessage)
  );

  let result = generatedErrors;

  if (hasNodeNumberError) {
    result = generatedErrors
      .filter(
        ({ errorMessage }) =>
          errorMessage !==
          DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.NODE_NUMBER
      )
      // Normalise whichever wording the backend used into our single message.
      // `deriveNodeNumberErrors` then strips them all and re-emits one per
      // offending row, so the popover shows one row per CELL rather than the
      // server's summary plus our per-cell pair.
      .map((error) =>
        isApiDuplicateNodeNumberMessage(error.errorMessage)
          ? {
              ...error,
              errorMessage: DUPLICATE_NODE_NUMBER_MESSAGE,
            }
          : error
      );
  }

  // PSTUDIO-3990: the server path derives the SAME two classes as the store
  // computeds, so a compile result and an edit-time derivation can never
  // disagree about what is wrong with a node number.
  return deriveNodeNumberErrors(rows, result, context);
};
